<script lang="ts">
import { defineComponent, PropType, toRef, inject, ref, watch } from 'vue'
import SelectorForm from '@/components/form/SelectorForm.vue'
import MultiSelectorForm from '@/components/form/MultiSelectorForm.vue'
import userCareer from '@/models/userCareer'
import wait from '@/utilities/wait'

export default defineComponent({
  name: 'SelectorProfessionWithDomain',

  components: {
    SelectorForm,
    MultiSelectorForm,
  },

  props: {
    profession: {
      type: Object as PropType<InputText>,
      required: true,
    },

    professionDomain: {
      type: Object as PropType<InputMultiSelect>,
      required: true,
    },

    namespace: {
      type: String,
      default: '',
    },

    defaultSelectorLabel: {
      type: String,
      default: '選択してください',
    },

    defaultSelectorValue: {
      type: String,
      default: '',
    },

    header: {
      type: String,
      default: '職種',
    },
  },

  emits: [
    'update:profession',
    'change:professionDomain',
    'update:professionDomain',
    'open:professionDomain',
    'close:professionDomain',
  ],

  setup(props, { emit }) {
    const { PROFESSION_DOMAINS, isShowProfessionDomain } = userCareer(toRef({ profession: props.profession }))

    // constants
    const PROFESSIONS = inject<string[]>('PROFESSIONS')!
    const TRANSITION_DURATION = '0.3s' as const
    const TRANSITION_PHASE = {
      READY: 0,
      DOING: 1,
      DONE: 2,
    } as const

    const transitionObserver = ref({
      run: false,
      phase: 0,
    })

    // 選択肢に存在しない値の場合は空の値で再設定
    // ※ 古いユーザの場合、古い区分で値を保持している場合があり、その場合は強制的に再設定させる。
    watch(() => props.profession.value, () => {
      if (props.profession.value && !PROFESSIONS.includes(props.profession.value)) {
        emit('update:profession', '')
        emit('update:professionDomain', '')
      }
    })

    watch(() => isShowProfessionDomain.value, () => {
      transitionObserver.value.run = true
    })

    watch(() => transitionObserver.value.run, async (newValue, oldValue) => {
      if (!oldValue && newValue) {
        transitionObserver.value.phase = TRANSITION_PHASE.DOING

        // 「担当領域」表示アコーディオンの CSS transition の完了を待機したら完了ステータスにして後置処理を行う
        await wait(parseFloat(TRANSITION_DURATION))
        transitionObserver.value.phase = TRANSITION_PHASE.DONE
      }
    })

    watch(() => transitionObserver.value.phase, () => {
      // CSS transition 完了後の後置処理
      if (transitionObserver.value.phase === TRANSITION_PHASE.DONE) {
        onTransitionEnd()
        transitionObserver.value.run = false
        transitionObserver.value.phase = TRANSITION_PHASE.READY
      }
    })

    const emitProfession = (value: string) => {
      if (typeof value === 'string') {
        emit('update:profession', value)
      } else {
        emit('update:profession', '')
      }
    }

    const emitProfessionDomain = (value: Array<string>) => {
      if (Array.isArray(value) && !!value.length) {
        emit('update:professionDomain', value)
      } else {
        emit('update:professionDomain', [])
      }
    }

    /**
     * 「職種」の選択肢が変更された際に発火するイベント
     *
     * @param professionValue 選択された「職種」の値
     */
    const onSelectedProfession = (professionValue: string): void => {
      // 「職種」が変更されたら、現在選択している「担当領域」の選択は解除する
      if (props.profession.value !== professionValue) {
        emitProfessionDomain([])
      }

      emitProfession(professionValue)
    }

    /**
     * アコーディオンがアニメーションが完了しきった際に発火するイベント
     *
     * @description
     *  追加入力項目の input オブジェクトを state から削除する（あるいは操作する）タイミングは、
     *  親選択肢が変更された瞬間ではなく、アコーディオンのアニメーションが完了しきったあとに発火させないと、
     *  アコーディオンのトランジションが上手く動かなくなるため（state の情報を先に消すとアコーディオンの中身が消えてしまうため）、
     *  state の操作処理は、明示的にトランジションが完了したあとに遅延発火させる
     */
    const onTransitionEnd = () => {
      const emitName = isShowProfessionDomain.value
        ? 'open:professionDomain'
        : 'close:professionDomain'
      emit(emitName)
    }

    return {
      // constants
      PROFESSIONS,
      PROFESSION_DOMAINS,
      TRANSITION_DURATION,
      TRANSITION_PHASE,

      // data
      transitionObserver,

      // computed
      isShowProfessionDomain,

      // methods
      emitProfession,
      emitProfessionDomain,
      onSelectedProfession,
      onTransitionEnd,
    }
  }
})
</script>

<template>
  <div class="div">
    <SelectorForm
      :model-value="profession.value"
      :items="PROFESSIONS"
      :default-selector-label="defaultSelectorLabel"
      :default-selector-value="defaultSelectorValue"
      :namespace="namespace"
      :name="profession.name"
      :header="header"
      :validation-scheme="profession.validationScheme"
      @update:model-value="onSelectedProfession($event)" />

    <div
      :class="[
        'l-unit__professionDomain',
        { 'is-active': transitionObserver.phase === TRANSITION_PHASE.READY }
      ]"
      :style="{ '--transition-duration': TRANSITION_DURATION }"
      :data-open="isShowProfessionDomain">
      <div class="l-unit__professionDomain--action">
        <MultiSelectorForm
          :model-value="professionDomain.value"
          :items="PROFESSION_DOMAINS[profession.value] || []"
          :namespace="namespace"
          :name="professionDomain.name"
          :validation-scheme="professionDomain.validationScheme"
          header="担当している業務領域（複数選択）"
          @update:model-value="emitProfessionDomain($event)">
          <div class="o-note__general u-text__small u-margin__smaller">
            <p class="o-note__general--item">実務・ツール選定/導入など関与している事柄。</p>
          <!-- /.o-note__general --></div>
        </MultiSelectorForm>
      <!-- /.l-unit__professionDomain action --></div>
    <!-- /.l-unit__professionDomain --></div>
  </div>
</template>

<style lang="sass" scoped>
.l-unit__professionDomain
  $root: &
  --transition-duration: 1.3s
  display: grid
  grid-template-rows: 0fr
  transition:
    property: grid-template-rows
    duration: var(--transition-duration)
    timing-function: ease

  &[data-open="true"]
    grid-template-rows: 1fr
    overflow: visible

    &.is-active
      #{$root}--action
        overflow: visible

  & &--action
    overflow: hidden

    /**
     *「職種」と「担当領域」の間のマージン
     * 単純に margin（または padding）を設定すると、非表示時も隙間が空いてしまうので、疑似要素で擬似的マージンを空ける
     */
    &::before
      content: ""
      height: 1em
      display: block
</style>
